import useCustomHook from '@/hooks/useCustomHook';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { useContext, useEffect, useRef } from 'react';
import { PaymentFormContext } from '../../PaymentFormContext';

export default function useNewStripePaymentMethodForm({
  paymentIntentId,
  subscriptionTypeId,
  amount,
  subscriptionParams,
  setFuturePayments,
  promotionCode,
  elementsRef,
}) {
  const paymentFormContext = useContext(PaymentFormContext);
  const stripe = useStripe();
  const elements = useElements();
  const autoRenewRef = useRef(subscriptionParams.autoRenew);
  elementsRef.current = elements;

  const { state, actions } = useCustomHook({
    defaultState: {
      loading: false,
      processing: false,
      error: null,
      isPaymentReady: false,
    },
    actions: {
      setPaymentReady: (isPaymentReady) => {
        actions.setState({ isPaymentReady });
      },
      pay: async (event) => {
        actions.setState({ processing: true, error: null });
        event.preventDefault();

        if (!stripe || !elements) {
          return;
        }

        try {
          const subscriptionPayment =
            await paymentFormContext.actions.createSubscriptionPayment({
              paymentIntentId,
              subscriptionTypeId,
              amount,
              subscriptionParams,
              couponCode: promotionCode?.code,
            });

          const successUrl =
            paymentFormContext.actions.onPaymentCompletedUrl(
              subscriptionPayment
            );
          const result = await stripe.confirmPayment({
            elements,
            confirmParams: {
              return_url: successUrl,
            },
          });

          if (result.error) {
            actions.setState({
              error: result.error.message,
              processing: false,
            });
          } else {
            paymentFormContext.actions.onPaymentCompleted();
            actions.setState({ processing: false });
          }
        } catch (e) {
          actions.setState({
            processing: false,
            error: e.response?.data?.message || e.message,
          });
        }
      },
      closeError: () => {
        actions.setState({ error: null });
      },
    },
  });

  useEffect(() => {
    if (subscriptionParams.autoRenew != autoRenewRef.current) {
      autoRenewRef.current = subscriptionParams.autoRenew;
      setFuturePayments(subscriptionParams.autoRenew);
    }
  }, [subscriptionParams.autoRenew]);

  return { state: { ...state, stripe }, actions };
}
